import { useCallback } from 'react';
import type { EditorStarterItem } from '../../../items/item-type';
import { isLeftClick } from '../../../utils/is-left-click';
import {
  adjustSelectionAfterClick,
  calculateSelectionAndDragState,
} from '../../../utils/selection-utils';
import { setSelectedItems } from '../../../state/actions/set-selected-items';
import { setActiveSelectionSurface } from '../../../state/actions/set-active-selection-surface';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '../../../utils/use-context';
import { useTimelineSize } from '../use-timeline-size';
import { consumeMoveClick, startPointerMove } from '@waka/compat/pointer-move';
import { useDragOverlay } from '../../../drag-overlay-provider';
import { flushSync } from 'react-dom';
import { focusEditingSurface } from '@waka/compat/focus-editing-surface';

export const useItemDrag = ({
  draggedItem,
  isLocked = false,
}: {
  draggedItem: EditorStarterItem;
  isLocked?: boolean;
}) => {
  const { setState } = useWriteContext();
  const { timelineWidth } = useTimelineSize();
  const stateAsRef = useCurrentStateAsRef();
  const { startDrag, updateCursorPosition, stopDrag, setSnappedPositions } =
    useDragOverlay();
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isLocked || consumeMoveClick(stateAsRef, event.timeStamp)) return;
      const selection = adjustSelectionAfterClick({
        clickedItem: draggedItem,
        currentSelectedItemIds: stateAsRef.current.selectedItems,
        isMultiSelectMode: event.metaKey || event.shiftKey,
      });
      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(state, selection),
            'timeline',
          ),
        commitToUndoStack: false,
      });
    },
    [draggedItem, isLocked, setState, stateAsRef],
  );
  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isLeftClick(event)) return;
      event.stopPropagation();
      if (isLocked || timelineWidth === null) return;
      focusEditingSurface(event.currentTarget, event.target, () =>
        flushSync(() => {}),
      );
      const selected = stateAsRef.current.selectedItems;
      const { allowDrag, newSelectedItems } = calculateSelectionAndDragState({
        clickedItem: draggedItem,
        currentSelectedItemIds: selected,
        isMultiSelectMode: event.metaKey || event.shiftKey,
      });
      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(state, newSelectedItems),
            'timeline',
          ),
        commitToUndoStack: false,
      });
      if (allowDrag)
        startPointerMove({
          event,
          stateRef: stateAsRef,
          setState,
          itemId: draggedItem.id,
          ids: selected.includes(draggedItem.id) ? selected : [draggedItem.id],
          timelineWidth,
          overlay: {
            startDrag,
            updateCursorPosition,
            stopDrag,
            setSnappedPositions,
          },
        });
    },
    [
      draggedItem,
      isLocked,
      setState,
      stateAsRef,
      timelineWidth,
      startDrag,
      updateCursorPosition,
      stopDrag,
      setSnappedPositions,
    ],
  );
  return { onPointerDown, onClick };
};
